// /hvac-consultant: HVAC consultants in Delhi NCR, booked and paid online.
//
// Four services, priced in src/data/consultation.js (GST included):
//   video call ₹99 · site visit + 1-hr consultation ₹1,500 ·
//   heat load ₹0.50 a sq ft (min ₹750) · end-to-end design ₹3 / ₹4 a sq ft
// The server prices the payment again from the same file
// (api/consult-order.js); only a verified payment shows as paid.
//
// Below the booking: the long-form guide (src/data/consultantContent.js),
// the sample heat load report, a free toolkit, links and questions. The
// pre-render writes the same text for Google.
//
// ?service=call|visit|heatload|design picks the service; ?sqft= fills the
// area; ?type=home|commercial the property; ?from= where the customer came from.

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import Helmet from '../lib/Helmet';
import LongForm from '../components/LongForm';
import { formatINR, whatsAppLink } from '../lib/quote';
import { track, FUNNEL } from '../lib/track';
import { startConsultPayment } from '../lib/razorpay';
import { BUSINESS } from '../data/pricing';
import {
  CONSULT_SERVICES, CONSULT_MAX_SQFT, CONSULT_SEO, CONSULT_CALLBACK, PROPERTY_TYPES,
  consultService, consultPrice, consultRate, propertyType,
} from '../data/consultation';
import { CONSULTANT_SECTIONS, CONSULTANT_TOOLKIT, CONSULTANT_FAQ } from '../data/consultantContent';
import { INTERNAL_LINKS, EXTERNAL_LINKS, DESIGNER_SLUG } from '../data/designerLibrary';

const inputCls = 'h-11 w-full rounded-lg border border-gray-300 px-3 text-base text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const MAX_TEXT = CONSULT_MAX_SQFT.toLocaleString('en-IN');
const rateText = (r) => `₹${r % 1 ? r.toFixed(2) : r}`;

const ChatIcon = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.5A8.4 8.4 0 1 1 21 11.5z" />
  </svg>
);

// The price, as it reads on a card: "₹99 ₹1,000", "₹0.50/sq ft ₹2", "₹3 · ₹4".
function Price({ s, big = false }) {
  const num = big ? 'text-xl sm:text-[26px]' : 'text-lg';
  if (s.price != null) {
    return (
      <span className="whitespace-nowrap">
        {s.was && <s className="mr-1.5 text-xs text-gray-500">{formatINR(s.was)}</s>}
        <span className={`${num} font-extrabold text-gray-900`}>{formatINR(s.price)}</span>
      </span>
    );
  }
  if (s.rate != null) {
    return (
      <span className="whitespace-nowrap">
        {s.wasRate && <s className="mr-1.5 text-xs text-gray-500">{rateText(s.wasRate)}</s>}
        <span className={`${num} font-extrabold text-gray-900`}>{rateText(s.rate)}</span>
        <span className="text-xs font-semibold text-gray-600">/sq ft</span>
      </span>
    );
  }
  return (
    <span className="whitespace-nowrap">
      <span className={`${num} font-extrabold text-gray-900`}>{rateText(s.rates.home)}–{rateText(s.rates.commercial).slice(1)}</span>
      <span className="text-xs font-semibold text-gray-600">/sq ft</span>
    </span>
  );
}

// The booking form. One state, shown in the hero on a laptop and below the
// prices on a phone.
function Booking({ f, set, onPay, busy, error, paid, className = '', idp = 'm' }) {
  const s = consultService(f.svc);
  const byArea = s.price == null;
  const area = Math.floor(Number(String(f.sqft).replace(/[,\s]/g, '')));
  const type = s.byType ? propertyType(f.type) : null;
  const quoteType = Boolean(type && type.quote);
  const over = byArea && area > CONSULT_MAX_SQFT;
  const total = quoteType ? null : consultPrice(f.svc, area, f.type);
  const rate = consultRate(f.svc, f.type);
  const waHref = whatsAppLink(`Hi, I'd like a quote for ${s.name.toLowerCase()}${type ? ` for a ${type.long.toLowerCase()}` : ''}${area > 0 ? `, ${area.toLocaleString('en-IN')} sq ft` : ''}.`);
  const needsQuote = quoteType || over;

  if (paid) {
    return (
      <section aria-live="polite" className={`rounded-2xl border-2 border-green-600 bg-green-50 p-5 text-gray-900 ${className}`}>
        <p className="text-[11px] font-extrabold tracking-wide text-green-700">PAYMENT RECEIVED</p>
        <p className="mt-1 text-xl font-extrabold">{formatINR(paid.total)} for {paid.service_name}</p>
        <p className="mt-2 text-sm leading-relaxed text-gray-800">
          {CONSULT_CALLBACK} We&apos;ll call you on {paid.mobile.slice(0, 5)} {paid.mobile.slice(5)}.
        </p>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-gray-600">Reference</dt><dd className="font-mono font-semibold">{paid.ref}</dd>
          <dt className="text-gray-600">Payment ID</dt><dd className="font-mono">{paid.payment_id}</dd>
        </dl>
        <p className="mt-3 text-xs leading-relaxed text-gray-700">If you buy the system from us, the whole fee comes off your bill. Quote {paid.ref} when you order.</p>
      </section>
    );
  }

  return (
    <section aria-label="Book a consultation" className={`flex flex-col gap-3.5 rounded-2xl bg-white p-4 text-gray-900 shadow-[0_20px_50px_rgba(0,0,0,0.18)] ring-1 ring-gray-200 sm:p-5 ${className}`}>
      <p className="text-lg font-extrabold">Book a consultation</p>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="sr-only">Service</legend>
        {CONSULT_SERVICES.map((x) => {
          const on = x.id === f.svc;
          return (
            <label key={x.id} className={`flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 ${on ? 'border-2 border-blue-700 bg-blue-50 px-[11px] py-[9px]' : 'border border-gray-200 hover:border-blue-300'}`}>
              <input type="radio" name="consult-service" checked={on} onChange={() => set({ svc: x.id })} className="h-4 w-4 shrink-0 accent-blue-700" />
              <span className="min-w-0 flex-1 text-sm font-semibold">{x.name}</span>
              <Price s={x} />
            </label>
          );
        })}
      </fieldset>

      {s.byType && (
        <div>
          <p className="mb-1.5 text-[13px] font-semibold">Type of property</p>
          <div className="grid grid-cols-3 gap-1.5" role="group" aria-label="Type of property">
            {PROPERTY_TYPES.map((t) => (
              <button key={t.id} type="button" aria-pressed={f.type === t.id} onClick={() => set({ type: t.id })}
                className={`h-10 rounded-lg text-[13px] ${f.type === t.id ? 'bg-blue-700 font-bold text-white' : 'border border-gray-300 bg-white text-gray-700'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {byArea && !quoteType && (
        <div>
          <label htmlFor={`area-${idp}`} className="mb-1.5 block text-[13px] font-semibold">Area to air-condition (sq ft)</label>
          <input id={`area-${idp}`} inputMode="numeric" autoComplete="off" value={f.sqft}
            onChange={(e) => set({ sqft: e.target.value.replace(/[^\d]/g, '').slice(0, 6) })} placeholder="e.g. 1200" className={inputCls} />
          {area > 0 && !over && total != null && rate != null && (
            <p className="mt-1.5 text-xs text-gray-600">
              {area.toLocaleString('en-IN')} sq ft × {rateText(rate)} = {formatINR(Math.ceil(area * rate))}
              {s.min && Math.ceil(area * rate) < s.min ? <> · minimum <b className="text-gray-900">{formatINR(s.min)}</b></> : <> → <b className="text-gray-900">{formatINR(total)}</b></>}
            </p>
          )}
        </div>
      )}

      {needsQuote && (
        <div className="rounded-xl bg-amber-50 p-3">
          <p className="text-[13px] font-semibold text-amber-900">
            {quoteType ? 'For hotels we quote separately.' : `Above ${MAX_TEXT} sq ft we quote separately.`}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={() => track(FUNNEL.WHATSAPP_HANDOFF, { source: 'consult_quote' })}
              className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-green-600 px-3.5 text-[13px] font-semibold text-white">
              <ChatIcon className="h-4 w-4" /> WhatsApp us
            </a>
            <a href={`tel:${BUSINESS.phone}`} onClick={() => track(FUNNEL.PHONE_CLICK, { source: 'consult_quote' })}
              className="inline-flex h-10 items-center rounded-lg border-2 border-blue-700 px-3.5 text-[13px] font-semibold text-blue-700">
              Call {BUSINESS.phoneDisplay.replace('+91 ', '')}
            </a>
          </div>
        </div>
      )}

      {!needsQuote && (
        <>
          <div className={`grid gap-2.5 ${s.ncrOnly ? 'grid-cols-2' : 'grid-cols-2'}`}>
            <div className={s.ncrOnly ? 'col-span-2' : ''}>
              <label htmlFor={`nm-${idp}`} className="mb-1.5 block text-[13px] font-semibold">Your name</label>
              <input id={`nm-${idp}`} autoComplete="name" value={f.name} onChange={(e) => set({ name: e.target.value.slice(0, 80) })} className={inputCls} />
            </div>
            <div>
              <label htmlFor={`ph-${idp}`} className="mb-1.5 block text-[13px] font-semibold">Mobile</label>
              <input id={`ph-${idp}`} type="tel" inputMode="numeric" autoComplete="tel-national" value={f.phone}
                onChange={(e) => set({ phone: e.target.value.replace(/[^\d+\s]/g, '').slice(0, 14) })} className={inputCls} />
            </div>
            {s.ncrOnly && (
              <div>
                <label htmlFor={`pin-${idp}`} className="mb-1.5 block text-[13px] font-semibold">Site pincode</label>
                <input id={`pin-${idp}`} inputMode="numeric" autoComplete="postal-code" value={f.pin}
                  onChange={(e) => set({ pin: e.target.value.replace(/\D/g, '').slice(0, 6) })} className={inputCls} />
              </div>
            )}
          </div>
          {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">{error}</p>}
          <button type="button" onClick={onPay} disabled={busy}
            className="h-12 rounded-xl bg-blue-700 text-base font-bold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-300">
            {busy ? 'Opening payment…' : total != null ? `Pay ${formatINR(total)}` : 'Pay'}
          </button>
          <p className="text-center text-xs leading-relaxed text-gray-600">UPI, cards or netbanking. {CONSULT_CALLBACK}</p>
        </>
      )}
    </section>
  );
}

export default function Consultation() {
  const [params] = useSearchParams();
  const first = consultService(params.get('service')) ? params.get('service') : 'call';
  const from = (params.get('from') || '').slice(0, 30);
  const [f, setF] = useState(() => {
    const n = Math.floor(Number(params.get('sqft')));
    const t = propertyType(params.get('type'));
    return { svc: first, sqft: n > 0 ? String(n) : '', type: t ? t.id : 'home', name: '', phone: '', pin: '' };
  });
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setError(''); };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(null);

  const pick = (id) => {
    set({ svc: id });
    const el = window.innerWidth >= 1024 ? document.getElementById('top') : document.getElementById('book');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pay = () => {
    const s = consultService(f.svc);
    const area = Math.floor(Number(String(f.sqft).replace(/[,\s]/g, '')));
    const mobile = f.phone.replace(/\D/g, '').slice(-10);
    const gaps = [];
    if (s.price == null && !(area >= 1)) gaps.push('the area in sq ft');
    if (f.name.trim().length < 2) gaps.push('your name');
    if (!/^[6-9]\d{9}$/.test(mobile)) gaps.push('a 10-digit mobile number');
    if (s.ncrOnly && !/^\d{6}$/.test(f.pin.trim())) gaps.push('the site pincode');
    if (gaps.length) { setError(`Add ${gaps.join(', ')}.`); return; }
    const total = consultPrice(f.svc, area, f.type);
    if (total == null) return;
    setBusy(true);
    track(FUNNEL.CONSULT_STARTED, { service: f.svc, sqft: s.price == null ? area : 0, value: total, source: from });
    startConsultPayment({
      service: f.svc,
      sqft: s.price == null ? area : undefined,
      type: s.byType ? f.type : undefined,
      pincode: s.ncrOnly ? f.pin.trim() : undefined,
      customer: { name: f.name.trim(), phone: mobile },
      source: from,
      onPaid: (r) => {
        setBusy(false);
        setPaid({ ...r, mobile });
        track(FUNNEL.CONSULT_PAID, { service: f.svc, value: r.total, quote_ref: r.ref });
      },
      onFailed: (msg) => setError(`${msg} Try again, or use another payment method.`),
      onCancelled: () => setBusy(false),
      onError: (msg) => { setBusy(false); setError(msg); },
    });
  };

  const bookingProps = { f, set, onPay: pay, busy, error, paid };
  const toc = [
    ['prices', 'HVAC consultation cost'],
    ...CONSULTANT_SECTIONS.map((x) => [x.id, x.title]),
    ['toolkit', 'Free toolkit'],
    ['links', 'Important links'],
    ['questions', 'Questions'],
  ];

  return (
    <>
      <Helmet>
        <title>{CONSULT_SEO.title}</title>
        <meta name="description" content={CONSULT_SEO.meta} />
      </Helmet>

      <main className="pt-16 pb-16 lg:pt-20">
        {/* Hero: blueprint band. On a laptop the booking sits right in it. */}
        <section id="top" className="scroll-mt-20 bg-[#172554] bg-[linear-gradient(rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.07)_1px,transparent_1px)] bg-[length:24px_24px] text-white">
          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-8 sm:py-10 lg:grid-cols-[1.15fr_1fr] lg:items-start lg:py-14">
            <div>
              <nav className="text-[11px] text-blue-200 sm:text-xs">
                <Link to="/" className="hover:text-white">Home</Link>
                <span className="mx-1.5">›</span>
                <span className="text-white">HVAC Design</span>
              </nav>
              <p className="mt-4 text-[11px] font-bold tracking-[0.18em] text-blue-300 sm:text-xs">{CONSULT_SEO.eyebrow}</p>
              <h1 className="mt-2 text-[32px] font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-[52px]">{CONSULT_SEO.h1}</h1>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-blue-100 sm:text-[17px]">{CONSULT_SEO.intro}</p>
              <div className="mt-5 grid max-w-lg grid-cols-3 gap-2 sm:gap-3">
                {[[String(BUSINESS.since), 'Cooling Delhi since'], ['1,500+', 'Projects done'], ['#1', 'Daikin dealer, North Region']].map(([a, b]) => (
                  <div key={b} className="rounded-xl border border-white/20 px-3 py-2.5 sm:px-4">
                    <p className="text-lg font-extrabold sm:text-2xl">{a}</p>
                    <p className="mt-0.5 text-[10px] leading-snug text-blue-200 sm:text-xs">{b}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row lg:hidden">
                <button type="button" onClick={() => pick('call')}
                  className="h-12 rounded-xl bg-white px-5 text-[15px] font-extrabold text-[#1e3a8a]">Book a ₹99 video call</button>
                <a href="#prices" className="flex h-12 items-center justify-center rounded-xl border border-white/60 px-5 text-sm font-semibold text-white">See all prices</a>
              </div>
              <p className="mt-4 flex items-center gap-1.5 text-xs text-blue-200 sm:text-[13px]">
                <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" /> Fee adjusted against your order · Prices include GST
              </p>
            </div>
            <Booking {...bookingProps} idp="d" className="hidden lg:flex" />
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4">
          {/* Prices */}
          <section id="prices" className="scroll-mt-24 pt-8 sm:pt-12">
            <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">HVAC consultation cost</h2>
            <p className="mt-1 text-sm text-gray-600 sm:text-[15px]">Fixed prices, GST included. The whole fee comes off your bill if you buy the system from us.</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {CONSULT_SERVICES.map((s) => (
                <div key={s.id} className={`relative flex flex-col rounded-2xl p-4 ${s.featured ? 'border-2 border-blue-700 bg-blue-50' : 'border border-gray-200 bg-white'}`}>
                  {s.featured && <span className="absolute -top-2.5 left-4 rounded-full bg-blue-700 px-2 py-0.5 text-[10px] font-extrabold tracking-wide text-white">MOST COMPLETE</span>}
                  <div className="flex items-baseline justify-between gap-2 sm:block">
                    <p className="text-[15px] font-bold text-gray-900">{s.name}</p>
                    <p className="shrink-0 sm:mt-2"><Price s={s} big /></p>
                  </div>
                  {s.rates && (
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      {PROPERTY_TYPES.filter((t) => !t.quote).map((t) => (
                        <div key={t.id} className="rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-blue-100">
                          <p className="text-[11px] text-gray-600">{t.id === 'home' ? 'Homes' : 'Offices, shops'}</p>
                          <p className="text-base font-extrabold text-gray-900">{rateText(s.rates[t.id])}<span className="text-[11px] font-semibold text-gray-600">/sq ft</span></p>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="mt-1.5 flex-1 text-[13px] leading-relaxed text-gray-600 sm:mt-2">{s.blurb}</p>
                  {s.rates && <p className="mt-1 text-[11px] text-gray-500">Hotels, and sites over {MAX_TEXT} sq ft: quoted.</p>}
                  <button type="button" onClick={() => pick(s.id)}
                    className={`mt-3 h-10 rounded-lg text-sm font-bold sm:mt-3 ${s.featured ? 'bg-blue-700 text-white' : 'border-2 border-blue-700 text-blue-700'}`}>
                    Book
                  </button>
                </div>
              ))}
            </div>
          </section>

          <div id="book" className="scroll-mt-20 pt-6 lg:hidden">
            <Booking {...bookingProps} />
          </div>

          {/* The guide, with a contents list beside it on a laptop */}
          <div className="mt-12 grid gap-10 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="min-w-0 max-w-3xl">
              <LongForm sections={CONSULTANT_SECTIONS} />

              <section id="designers" className="mt-10 scroll-mt-28">
                <Link to={`/${DESIGNER_SLUG}`} className="flex items-center gap-3 rounded-2xl bg-orange-50 p-4 hover:bg-orange-100">
                  <span className="min-w-0 flex-1">
                    <span className="block text-base font-bold text-gray-900">Architect or interior designer?</span>
                    <span className="mt-0.5 block text-sm text-gray-700">Free HVAC design when we do the job, a referral fee or trade pricing, and a library of answers for designers.</span>
                  </span>
                  <span className="text-sm font-bold text-orange-700">For designers ›</span>
                </Link>
              </section>

              <section id="toolkit" className="mt-10 scroll-mt-28">
                <h2 className="text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl">Free toolkit</h2>
                <p className="mt-1 text-[15px] text-gray-700">Use these before you call anyone, us included.</p>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {CONSULTANT_TOOLKIT.map((t) => {
                    const inner = (
                      <>
                        <span className="block text-sm font-bold text-gray-900">{t.label}{t.external && <span className="ml-1 text-xs font-normal text-gray-500">↗</span>}</span>
                        <span className="mt-1 block text-xs leading-relaxed text-gray-600">{t.text}</span>
                      </>
                    );
                    const cls = 'block h-full rounded-xl border border-gray-200 p-3.5 hover:border-blue-400';
                    return (
                      <li key={t.label}>
                        {t.external
                          ? <a href={t.href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
                          : <Link to={t.href} className={cls}>{inner}</Link>}
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section id="links" className="mt-10 scroll-mt-28">
                <h2 className="text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl">Important links</h2>
                <div className="mt-4 grid gap-6 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">On our site</p>
                    <ul className="mt-2 flex flex-col gap-2.5">
                      {INTERNAL_LINKS.filter((l) => l.href !== '/hvac-consultant').map((l) => (
                        <li key={l.href}><Link to={l.href} className="text-sm font-semibold text-blue-700 hover:underline">{l.label}</Link><span className="block text-xs text-gray-600">{l.note}</span></li>
                      ))}
                      <li><Link to={`/${DESIGNER_SLUG}`} className="text-sm font-semibold text-blue-700 hover:underline">For architects and interior designers</Link><span className="block text-xs text-gray-600">HVAC library, ceiling depth checker, partner offers</span></li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Codes, standards and makers</p>
                    <ul className="mt-2 flex flex-col gap-2.5">
                      {EXTERNAL_LINKS.map((l) => (
                        <li key={l.href}><a href={l.href} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-blue-700 hover:underline">{l.label} ↗</a><span className="block text-xs text-gray-600">{l.note}</span></li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              <section id="questions" className="mt-10 scroll-mt-28">
                <h2 className="text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl">Questions</h2>
                <div className="mt-3 divide-y divide-gray-200 border-y border-gray-200">
                  {CONSULTANT_FAQ.map((x) => (
                    <details key={x.q} className="group">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3.5 text-left text-[15px] font-medium text-gray-900 [&::-webkit-details-marker]:hidden">
                        {x.q}
                        <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </summary>
                      <p className="pb-4 text-sm leading-relaxed text-gray-600">{x.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            </div>

            <aside className="hidden lg:block">
              <div className="sticky top-28 flex flex-col gap-4">
                <div className="rounded-2xl border border-gray-200 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">On this page</p>
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {toc.map(([id, label]) => (
                      <li key={id}><a href={`#${id}`} className="text-[13px] text-gray-700 hover:text-blue-700">{label}</a></li>
                    ))}
                  </ul>
                </div>
                <a href="#top" onClick={(e) => { e.preventDefault(); pick(f.svc); }}
                  className="flex h-12 items-center justify-center rounded-xl bg-blue-700 text-sm font-bold text-white hover:bg-blue-800">
                  Book a consultation
                </a>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}
